import { Loader2, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { type CSSProperties, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { Button, NumberInput } from '../../components/ui/index.js';
import { cartKey, useCartStore } from '../../lib/cartStore.js';
import { formatMoney } from '../../lib/money.js';

import { useBuyerPricing } from './buyerPricing.js';
import { useCartCurrencySync } from './cartPricing.js';
import { totalSaving, useCartSavings } from './cartSavings.js';
import { OriginalPrice } from './discountDisplay.js';
import { GroupPricingBadge } from './GroupPricingBadge.js';
import { StoreImage } from './StoreImage.js';

export function StorefrontCartPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const navigate = useNavigate();
  const cart = useCartStore();
  // The SHOPPER's currency rules the cart: their customer group's own currency
  // when they're signed in on one, else the store currency. Lines from an older
  // basis are re-priced (not silently mixed) by useCartCurrencySync.
  const buyer = useBuyerPricing();
  const { syncing } = useCartCurrencySync(buyer);
  const allLines = cart.slug === slug ? cart.items : [];
  // Until the basis is server-confirmed, show the cart in ITS own currency — a
  // failed lookup must never hide lines behind the store currency.
  const currency = buyer.authoritative ? buyer.currencyCode : allLines[0]?.currencyCode ?? buyer.currencyCode;
  const lines = allLines.filter((l) => l.currencyCode === currency);

  const money = (v: number): string => formatMoney(String(v), { locale: i18n.language, currency });
  // Display-only estimate; the server re-resolves every price at checkout.
  const subtotal = lines.reduce((sum, l) => sum + Number(l.price) * l.quantity, 0);
  // Automatic discounts are already inside every line price — this is only what
  // they took off, recorded when each line was added (see cartSavings.ts).
  const savingOf = useCartSavings(slug);
  const saved = totalSaving(lines, savingOf);
  // The store minimum is a STORE-currency amount, so it can only gate a
  // store-currency cart — the server skips it for a foreign-currency group too.
  const minOrder = currency === site.currencyCode && site.minOrderAmount ? Number(site.minOrderAmount) : 0;
  const belowMin = minOrder > 0 && subtotal < minOrder;

  // Animate a line out (collapse + fade) before the store actually drops it.
  // Reduced-motion users skip the delay and remove instantly.
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const removeLine = (k: string, name: string): void => {
    toast.success(t('cart.removed'), { description: name });
    if (reduceMotion) { cart.remove(k); return; }
    setRemoving((prev) => new Set(prev).add(k));
    window.setTimeout(() => {
      cart.remove(k);
      setRemoving((prev) => { const n = new Set(prev); n.delete(k); return n; });
    }, 300);
  };

  // Empty the whole (visible) cart — per-line removal so other-slug carts survive.
  const clearAll = (): void => {
    if (!window.confirm(t('cart.clearAllConfirm', { defaultValue: 'Remove all items from your cart?' }))) return;
    const keys = lines.map((l) => cartKey(l));
    toast.success(t('cart.clearedAll', { defaultValue: 'Cart cleared.' }));
    if (reduceMotion) { keys.forEach((k) => cart.remove(k)); return; }
    setRemoving(new Set(keys));
    window.setTimeout(() => {
      keys.forEach((k) => useCartStore.getState().remove(k));
      setRemoving(new Set());
    }, 300);
  };

  // Hold the totals while the shopper's basis (and the cart with it) settles —
  // showing a subtotal that's about to change currency is worse than a spinner.
  // An empty cart has nothing to re-price, so it never waits.
  const busy = allLines.length > 0 && (!buyer.resolved || syncing);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <Helmet><title>{`${t('cart.title')} · ${site.storeName}`}</title></Helmet>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold">{t('cart.title')}</h1>
          <GroupPricingBadge />
        </div>
        {lines.length > 0 && !busy && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-fg-muted transition hover:bg-danger/10 hover:text-danger"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            {t('cart.clearAll', { defaultValue: 'Clear all' })}
          </button>
        )}
      </div>

      {busy ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-bg-subtle py-16 text-center text-fg-muted">
          <Loader2 className="h-6 w-6 animate-spin text-brand" aria-hidden />
          <p className="text-sm">{t('cart.repricing', { defaultValue: 'Updating prices for your account…' })}</p>
        </div>
      ) : lines.length === 0 ? (
        <div className="sf-card flex flex-col items-center gap-4 rounded-2xl border border-border bg-bg-subtle py-16 text-center">
          <ShoppingBag className="h-10 w-10 text-fg-subtle" aria-hidden />
          <p className="text-fg-muted">{t('cart.empty')}</p>
          <Link to={`/s/${slug}/shop`} className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-brand-contrast transition hover:opacity-90">
            {t('store.browse')}
          </Link>
        </div>
      ) : (
        <>
          <ul className="flex flex-col divide-y divide-border rounded-2xl border border-border">
            {lines.map((l, idx) => {
              const k = cartKey(l);
              const saving = savingOf(l);
              return (
                <li key={k} style={{ ['--sf-delay']: `${idx * 40}ms` } as CSSProperties} className={`sf-card flex items-center gap-3 p-3 sm:gap-4 sm:p-4 ${removing.has(k) ? 'sf-cart-exit' : ''}`}>
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-bg-subtle">
                    <StoreImage src={l.imageUrl} alt={l.name} className="h-full w-full object-contain" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{l.name}</div>
                    <div className="flex flex-wrap items-baseline gap-x-1.5 font-mono text-xs tabular-nums text-fg-muted">
                      {money(Number(l.price))}
                      {saving && <OriginalPrice amount={saving.originalPrice} currency={currency} className="text-[0.7rem]" />}
                    </div>
                  </div>
                  <div className="inline-flex items-center rounded-lg border border-border">
                    <button type="button" onClick={() => cart.setQty(k, l.quantity - 1)} className="grid h-9 w-9 place-items-center text-fg-muted transition hover:text-fg active:scale-90 disabled:opacity-40 motion-reduce:transform-none" disabled={l.quantity <= 1} aria-label={t('cart.decrease')}>
                      <Minus className="h-4 w-4" aria-hidden />
                    </button>
                    {/* Unstyled: the border and height belong to the stepper this sits
                        inside. The native max never actually stopped anyone typing a
                        larger number — it only failed validation — so clamping here is
                        a real gain, not a replacement. */}
                    <NumberInput
                      value={String(l.quantity)}
                      onValueChange={(raw) => {
                        const n = Math.floor(Number(raw));
                        cart.setQty(k, Number.isFinite(n) && n >= 1 ? Math.min(n, 100000) : 1);
                      }}
                      unstyled
                      className="h-9 w-12 border-x border-border bg-bg text-center text-sm tabular-nums outline-none"
                      aria-label={t('cart.quantity')}
                    />
                    <button type="button" onClick={() => cart.setQty(k, l.quantity + 1)} className="grid h-9 w-9 place-items-center text-fg-muted transition hover:text-fg active:scale-90 motion-reduce:transform-none" aria-label={t('cart.increase')}>
                      <Plus className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                  <div className="w-20 text-end font-mono text-sm font-semibold tabular-nums"><span key={l.quantity} className="sf-pop inline-block">{money(Number(l.price) * l.quantity)}</span></div>
                  <button type="button" onClick={() => removeLine(k, l.name)} className="grid h-9 w-9 place-items-center rounded-md text-fg-muted transition hover:bg-bg-subtle hover:text-danger active:scale-90 motion-reduce:transform-none" aria-label={t('cart.remove')}>
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-col items-end gap-4 rounded-2xl border border-border bg-bg-subtle p-4">
            <div className="flex w-full items-center justify-between text-sm">
              <span className="text-fg-muted">{t('cart.subtotal')}</span>
              <span key={subtotal} className="sf-pop inline-block font-mono text-lg font-bold tabular-nums">{money(subtotal)}</span>
            </div>
            {saved > 0 && (
              // Past tense on purpose: the discount is already inside the
              // subtotal above, it is not deducted again at checkout.
              <div className="flex w-full items-center justify-between text-sm font-medium text-success">
                <span>{t('cart.saved')}</span>
                <span className="font-mono tabular-nums">{money(saved)}</span>
              </div>
            )}
            <p className="w-full text-end text-xs text-fg-muted">{t('cart.estimateNote')}</p>
            {belowMin && (
              <p className="w-full rounded-lg bg-danger/10 px-3 py-2 text-end text-xs font-medium text-danger">
                {t('cart.minOrderNotice', { defaultValue: 'Minimum order {{amount}}. Add {{remaining}} more to check out.', amount: money(minOrder), remaining: money(minOrder - subtotal) })}
              </p>
            )}
            <div className="flex w-full flex-wrap justify-end gap-3">
              <Link to={`/s/${slug}/shop`} className="inline-flex h-11 items-center rounded-md border border-border px-5 text-sm font-medium text-fg transition hover:bg-bg">
                {t('cart.continue')}
              </Link>
              <Button size="lg" variant="accent" disabled={belowMin} onClick={() => navigate(`/s/${slug}/checkout`)}>{t('cart.checkout')}</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
